import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import HrefLangTags from "../components/HrefLangTags";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CtaToggle from "../components/CtaToggle";
import ContactInfoCards from "../components/sections/ContactInfoCards";
import CTASection from "../components/sections/CTASection";
import MapEmbed from "../components/MapEmbed";
import Reveal from "../components/Reveal";
import { useLanguage } from "../contexts/LanguageContext";

const SERVICE_KEY_BY_SLUG = {
  "meta-ads": "services_options.meta_ads",
  "google-ads": "services_options.google_ads",
  seo: "services_options.seo",
  creative: "services_options.creative",
  social: "services_options.social",
  web: "services_options.web",
};

export default function ContactPage() {
  const { t, lang } = useLanguage();
  const location = useLocation();

  // Pre-select service from `?service=...` if provided
  const { defaultServiceSlug, defaultServiceLabel } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const slug = params.get("service") || "";
    const key = SERVICE_KEY_BY_SLUG[slug];
    return {
      defaultServiceSlug: slug,
      defaultServiceLabel: key ? t(key) : "",
    };
  }, [location.search, t]);

  const pageTitle = `${t("contact.hero_eyebrow")} · Yastık Medya`;
  const pageDesc = t("contact.hero_subtitle");

  return (
    <div data-testid="contact-page" className="min-h-screen bg-cream text-ink">
      <SeoHead title={pageTitle} description={pageDesc} path={`/${lang}/contact`} imageKey="contact" lang={lang} />
      <HrefLangTags pathSuffix="contact" />
      <Header />

      <main>
        {/* Hero */}
        <section data-testid="contact-hero" className="relative bg-cream pt-12 pb-section sm:pt-20 overflow-hidden">
          <div className="pointer-events-none absolute -top-32 end-[-12%] h-[520px] w-[520px] rounded-full bg-gold/15 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 bg-grain opacity-50" />
          <div className="container-page relative">
            <motion.span
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="eyebrow"
            >
              <Sparkles className="h-3 w-3 text-gold" />
              {t("contact.hero_eyebrow")}
            </motion.span>
            <h1
              className="mt-6 font-display text-display-1 font-semibold text-ink leading-[0.98] max-w-3xl"
              style={{ fontVariationSettings: '"opsz" 144' }}
              data-testid="contact-hero-title"
            >
              {t("contact.hero_title_pre")}{" "}
              <span className="gold-foil-text">{t("contact.hero_title_accent")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-body-lg text-ink/65 leading-relaxed">
              {t("contact.hero_subtitle")}
            </p>
          </div>
        </section>

        {/* Two-column: form + cards */}
        <section data-testid="contact-main-section" className="bg-cream py-section border-t border-mist">
          <div className="container-page grid gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <CtaToggle prefilled={`Merhaba, ${defaultServiceLabel || "hizmetleriniz"} hakkında bilgi almak istiyorum.`}>
                <CTASection
                  hideHeader={true}
                  eyebrow=""
                  title=""
                  titleAccent=""
                  subtitle=""
                  defaultServiceSlug={defaultServiceSlug}
                  defaultServiceLabel={defaultServiceLabel}
                />
              </CtaToggle>
            </div>
            <div className="lg:col-span-5">
              <ContactInfoCards />
            </div>
          </div>
        </section>

        {/* Map */}
        <section data-testid="contact-map-section" className="bg-cream py-section border-t border-mist">
          <div className="container-page">
            <Reveal as="div" className="max-w-2xl">
              <span className="eyebrow"><span className="h-1 w-1 rounded-full bg-gold" />{t("contact.map_eyebrow")}</span>
              <h2 className="mt-5 font-display text-display-2 font-semibold text-ink">
                {t("contact.map_title_pre")}{" "}
                <span className="gold-foil-text">{t("contact.map_title_accent")}</span>
              </h2>
            </Reveal>
            <Reveal as="div" delay={0.08} className="mt-10">
              <MapEmbed height={460} rounded="rounded-3xl" testId="contact-map" />
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
